import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Outlet,
  matchPath,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { ManagerTaskTooltip } from '@sorare/core/src/components/onboarding/managerTask/ManagerTaskTooltip';
import { MarketplaceOnboardingTask } from '@sorare/core/src/components/onboarding/managerTask/MarketplaceOnboardingTask';
import {
  FOOTBALL_MARKET_COMMON_PACKS,
  FOOTBALL_MARKET_GO_PRO,
  FOOTBALL_MARKET_INSTANT_BUY,
  FOOTBALL_MARKET_MANAGER_SALES,
  FOOTBALL_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
  FOOTBALL_MARKET_NEW_SIGNINGS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import {
  MarketplaceOnboardingStep,
  useManagerTaskContext,
} from '@sorare/core/src/contexts/managerTask';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';

import { MarketBanners } from '@sorare/marketplace/src/components/MarketBanners';
import { MarketHeader } from '@sorare/marketplace/src/components/MarketHeader';
import { DisabledMarket } from '@sorare/marketplace/src/components/market/DisabledMarket';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';

const MarketLayout = () => {
  const { setStep, task } = useManagerTaskContext();
  const navigate = useNavigate();
  const { isIosApp, canUseIosPostMessageAndForYou, isAndroidApp } =
    useIsMobileApp();
  const {
    flags: { buyOnWebMarketplaceFromAndroid = false },
  } = useFeatureFlags();
  const { currentUser } = useCurrentUserContext();

  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();
  const { pathname } = useLocation();
  const { showSimplifiedNav } = useSimplifiedNav(Sport.FOOTBALL);

  const isCommonPacksTab = !!matchPath(FOOTBALL_MARKET_COMMON_PACKS, pathname);

  const matchCommonMarketRoute = useMatch(
    catchAll(FOOTBALL_MARKET_COMMON_PACKS)
  );
  const disabledMarket =
    isAndroidApp && buyOnWebMarketplaceFromAndroid && !matchCommonMarketRoute;
  const content = (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <CurrentMarketplaceUserProvider sport={Sport.FOOTBALL}>
        {disabledMarket ? <DisabledMarket /> : <Outlet />}
      </CurrentMarketplaceUserProvider>
    </Suspense>
  );

  if (isIosApp) {
    return (
      <FullWidthContainer>
        <ContentWithStickyHeader
          header={
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              <Tab
                to={buildMarketTabRoute(
                  FOOTBALL_MARKET_INSTANT_BUY,
                  filterQuery
                )}
              >
                <FormattedMessage {...transferMarket.instantBuy} />
              </Tab>
              <Tab
                to={buildMarketTabRoute(
                  FOOTBALL_MARKET_NEW_SIGNINGS,
                  filterQuery
                )}
              >
                <FormattedMessage {...navLabels.auctions} />
              </Tab>
              {currentUser?.betaTesterFootballCommonCardsGame && (
                <Tab to={FOOTBALL_MARKET_COMMON_PACKS}>
                  <FormattedMessage {...navLabels.commonPacks} />
                </Tab>
              )}
            </TabBar>
          }
          hideHeader={!canUseIosPostMessageAndForYou}
        >
          {content}
        </ContentWithStickyHeader>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <ErrorBoundary fallback={null}>
        {!isCommonPacksTab && !showSimplifiedNav && <MarketBanners />}
      </ErrorBoundary>
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <FullWidthContainer>
              <EnterFromAndroidMarketPlaceTab />
              <NoIndex />
              <MarketHeader showCommonCoins={isCommonPacksTab} />
            </FullWidthContainer>
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              {showSimplifiedNav ? (
                <>
                  <Tab
                    to={FOOTBALL_MARKET_COMMON_PACKS}
                    matches={[FOOTBALL_MARKET_COMMON_PACKS]}
                  >
                    <FormattedMessage {...navLabels.amateurPacks} />
                  </Tab>
                  <Tab
                    to={FOOTBALL_MARKET_GO_PRO}
                    matches={[FOOTBALL_MARKET_GO_PRO]}
                  >
                    <FormattedMessage {...navLabels.pro} />
                  </Tab>
                </>
              ) : (
                <>
                  <Tab
                    to={buildMarketTabRoute(
                      FOOTBALL_MARKET_NEW_SIGNINGS,
                      filterQuery
                    )}
                  >
                    <FormattedMessage {...navLabels.auctions} />
                  </Tab>
                  <Tab
                    to={buildMarketTabRoute(
                      FOOTBALL_MARKET_INSTANT_BUY,
                      filterQuery
                    )}
                    matches={[FOOTBALL_MARKET_INSTANT_BUY]}
                  >
                    <FormattedMessage {...transferMarket.instantBuy} />
                  </Tab>
                  <ManagerTaskTooltip
                    name={MarketplaceOnboardingStep.managerSalesLink}
                    TaskComponent={MarketplaceOnboardingTask}
                    onClick={() => {
                      navigate(FOOTBALL_MARKET_MANAGER_SALES);
                      setStep(MarketplaceOnboardingStep.search);
                    }}
                    disable={!task}
                    placement="bottom"
                  >
                    <Tab
                      to={buildMarketTabRoute(
                        FOOTBALL_MARKET_MANAGER_SALES,
                        filterQueryWithInSeason
                      )}
                      matches={[
                        FOOTBALL_MARKET_MANAGER_SALES,
                        FOOTBALL_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
                      ]}
                    >
                      <FormattedMessage {...navLabels.purchases} />
                    </Tab>
                    {currentUser?.betaTesterFootballCommonCardsGame && (
                      <Tab
                        to={FOOTBALL_MARKET_COMMON_PACKS}
                        matches={[FOOTBALL_MARKET_COMMON_PACKS]}
                      >
                        <FormattedMessage {...navLabels.commonPacks} />
                      </Tab>
                    )}
                  </ManagerTaskTooltip>
                </>
              )}
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        {content}
      </ContentWithStickyHeader>
    </FullWidthContainer>
  );
};

export default MarketLayout;
